import { useState } from 'react';
import { Settings } from 'lucide-react';
import {
  DetailPanel,
  panelSpanFromLayout,
  type DetailPanelGridItem,
  type PanelSpan,
} from './DetailPanelGrid';
import { ParticipantExperienceSettingsModal } from './ParticipantExperienceSettingsModal';
import { useExperienceSettings } from '@/views/participant-experience/useExperienceSettings';
import type { ExperienceProfile } from '@nava/backoffice-bff-contracts';

function profileName(id: string | null | undefined, profiles: ExperienceProfile[]) {
  if (!id) return 'Product-line defaults only';
  const profile = profiles.find((item) => item.id === id);
  return profile
    ? `${profile.name}${profile.status === 'archived' ? ' (archived)' : ''}`
    : 'Unavailable profile';
}

export interface ParticipantExperienceTileProps {
  organizationId: string;
  study?: string | null;
  disabled?: boolean;
  panelId?: string;
  currentSpan?: PanelSpan;
}

export function ParticipantExperienceTile({
  organizationId,
  study = null,
  disabled = false,
  panelId,
  currentSpan,
}: ParticipantExperienceTileProps) {
  const [open, setOpen] = useState(false);
  const { profiles, defaults, binding } = useExperienceSettings(organizationId, study);
  const relevant = study ? binding : defaults;
  const error = profiles.error || relevant.error;
  return (
    <>
      <DetailPanel
        className="[&_[data-tile-header-actions]]:opacity-100 [&_.tile-drag-header_h2]:pr-10"
        title="Participant experience profiles"
        span="2x2"
        panelId={panelId}
        currentSpan={currentSpan}
        headerActions={
          <button
            type="button"
            aria-label="Participant experience settings"
            disabled={disabled}
            onClick={() => setOpen(true)}
            className="focus-ring rounded-lg p-2 text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
          >
            <Settings className="size-4" />
          </button>
        }
      >
        {error ? (
          <p role="alert" className="text-red-700">
            {error.message}
          </p>
        ) : profiles.isPending || relevant.isPending ? (
          <p>Loading profiles...</p>
        ) : (
          <div className="space-y-3 text-body">
            <dl className="space-y-3">
              {study ? (
                <div>
                  <dt className="font-semibold">Draft selection</dt>
                  <dd>{profileName(binding.data?.profileId, profiles.data ?? [])}</dd>
                </div>
              ) : (
                <>
                  <div>
                    <dt className="font-semibold">Insights</dt>
                    <dd>{profileName(defaults.data?.insights, profiles.data ?? [])}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Customer Success</dt>
                    <dd>{profileName(defaults.data?.customer_success, profiles.data ?? [])}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Active profiles</dt>
                    <dd>
                      {profiles.data?.filter((profile) => profile.status === 'active').length ?? 0}
                    </dd>
                  </div>
                </>
              )}
            </dl>
            {!profiles.data?.length && (
              <p className="text-small text-neutral-500">No custom profiles yet.</p>
            )}
            <p className="text-small text-neutral-500">
              {study
                ? 'Changes are saved to the draft. Apply to the live study separately; existing sessions keep their wording.'
                : 'Defaults apply to new studies. Existing studies keep their selection.'}
            </p>
          </div>
        )}
      </DetailPanel>
      {open && (
        <ParticipantExperienceSettingsModal
          key={`${organizationId}:${study}`}
          organizationId={organizationId}
          study={study}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function createParticipantExperienceTileItem(
  props: Pick<ParticipantExperienceTileProps, 'organizationId' | 'study' | 'disabled'>
): DetailPanelGridItem {
  return {
    id: 'participant-experience',
    title: 'Participant experience profiles',
    description: 'Participant wording profiles and settings.',
    layout: { x: 0, y: 25, w: 2, h: 2, minW: 2, minH: 2, isResizable: true },
    render: (layout) => (
      <ParticipantExperienceTile
        {...props}
        panelId="participant-experience"
        currentSpan={panelSpanFromLayout(layout, '2x2')}
      />
    ),
  };
}
